import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function BirdWatchingVancouverArticle() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "bird-watching-vancouver-guide";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = articles ? articles.find((art) => art.slug === ref_id) : null;

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Stanley Park",
    "section-2": "Queen Elizabeth Park",
    "section-3": "Jericho Beach Park",
    "section-4": "Pacific Spirit Regional Park",
    "section-5": "Iona Beach Regional Park",
    "section-6": "Reifel Migratory Bird Sanctuary",
    "section-7": "Maplewood Flats",
    "section-8": "Burnaby Lake Regional Park",
    "local-regulations": "Local Regulations & Best Practices",
    tips: "Bird-Watching Tips",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture/vancouver-bird-strategy.aspx",
      label: "City of Vancouver – Bird Strategy & Bird Week",
    },
    {
      url: "https://stanleyparkecology.ca/education/biodiversity/birds/",
      label: "Stanley Park Ecology Society (SPES)",
    },
    {
      url: "https://naturevancouver.ca/activities/birding/",
      label: "Nature Vancouver – Birding Section",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    // Stanley Park
    {
      id: "section-1",
      lat: 49.30000,
      lng: -123.14000,
      popupText: "Stanley Park: Lush forests and waterfowl habitats.",
    },
    // Queen Elizabeth Park
    {
      id: "section-2",
      lat: 49.24172,
      lng: -123.11275,
      popupText: "Queen Elizabeth Park: Urban oasis on highest point in Vancouver.",
    },
    // Jericho Beach Park
    {
      id: "section-3",
      lat: 49.27200,
      lng: -123.19200,
      popupText: "Jericho Beach Park: Coastal bird-watching near English Bay.",
    },
    // Pacific Spirit Regional Park
    {
      id: "section-4",
      lat: 49.25647,
      lng: -123.21885,
      popupText: "Pacific Spirit Regional Park: Extensive woodland trails.",
    },
    // Iona Beach Regional Park
    {
      id: "section-5",
      lat: 49.21700,
      lng: -123.19800,
      popupText: "Iona Beach Regional Park: Tidal flats and jetty walk.",
    },
    // Reifel Migratory Bird Sanctuary
    {
      id: "section-6",
      lat: 49.09750,
      lng: -123.16611,
      popupText: "George C. Reifel Migratory Bird Sanctuary: Key stopover for migratory birds.",
    },
    // Maplewood Flats
    {
      id: "section-7",
      lat: 49.30611,
      lng: -123.00500,
      popupText: "Maplewood Flats: North Shore conservation area with marshy habitat.",
    },
    // Burnaby Lake Regional Park
    {
      id: "section-8",
      lat: 49.24243,
      lng: -122.94607,
      popupText: "Burnaby Lake Regional Park: Large freshwater lake with diverse species.",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide to Bird-Watching in Vancouver: Best Locations & Tips</title>
        <meta
          name="description"
          content="Discover Vancouver's top bird-watching destinations, local regulations, and practical tips to observe wildlife ethically."
        />
        <meta
          name="keywords"
          content="bird-watching, Vancouver, wildlife, parks, tips"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/bird-watching-vancouver-guide.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide to Bird-Watching in Vancouver: Best Locations & Tips"
        />
        <meta
          property="og:description"
          content="Discover Vancouver's top bird-watching destinations, local regulations, and practical tips to observe wildlife ethically."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/bird-watching-vancouver-guide.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/bird-watching-vancouver-guide.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide to Bird-Watching in Vancouver: Best Locations & Tips"
        />
        <meta
          name="twitter:description"
          content="Discover Vancouver's top bird-watching destinations, local regulations, and practical tips to observe wildlife ethically."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/bird-watching-vancouver-guide.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Birding in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a paradise for bird-watchers, boasting a wide range of habitats—from lush coastal rainforests to expansive shoreline mudflats—that attract an incredible variety of bird species. Situated along the Pacific Flyway, the city hosts over 250 species of resident, migratory, and overwintering birds throughout the year. In this guide, we’ll explore Vancouver’s best birding locations, share tips and local guidelines, and answer common questions to help you make the most of your birding adventures.
            </p>

            {/* SECTION: Stanley Park */}
            <h2 id="section-1">Stanley Park</h2>
            <p ref={(el) => setSpotRef("section-1", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Stanley Park</strong>{" "}
              <Marker
                coordY={49.30000}
                coordX={-123.14000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Vancouver’s largest and most iconic park offers forests, wetlands, and shoreline all in one place. Its old-growth cedar and fir forests harbor woodpeckers, owls, and songbirds, while Lost Lagoon and Beaver Lake attract ducks, geese, and the occasional swan. Look for bald eagles nesting high in towering conifers and great blue herons near the lagoon. Birders can visit year-round: winter brings dozens of waterfowl species, and spring and fall feature a variety of migrating songbirds.
            </p>

            {/* SECTION: Queen Elizabeth Park */}
            <h2 id="section-2">Queen Elizabeth Park</h2>
            <p ref={(el) => setSpotRef("section-2", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Queen Elizabeth Park</strong>{" "}
              <Marker
                coordY={49.24172}
                coordX={-123.11275}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Situated at the highest point in Vancouver, this 130-acre horticultural park is a magnet for birds, especially during spring and fall migrations. The mix of exotic and native trees draws hummingbirds, warblers, and finches. Small ponds in the Quarry Gardens may host ducks, while the lush flowerbeds are perfect for spotting Anna’s Hummingbirds. The park is easily accessible by transit or car, with parking on-site.
            </p>

            {/* SECTION: Jericho Beach Park */}
            <h2 id="section-3">Jericho Beach Park</h2>
            <p ref={(el) => setSpotRef("section-3", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Jericho Beach Park</strong>{" "}
              <Marker
                coordY={49.27200}
                coordX={-123.19200}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – On Vancouver’s west side, Jericho combines a sandy beachfront, wetland, and forest in one compact area. A pond near the east end of the park is a hotspot for waterfowl, like Mallards and American Wigeons. The shoreline at low tide may reveal sandpipers and herons foraging, and the wooded patches are home to chickadees, woodpeckers, and owls. Arrive early to avoid crowds.
            </p>

            {/* SECTION: Pacific Spirit Regional Park */}
            <h2 id="section-4">Pacific Spirit Regional Park</h2>
            <p ref={(el) => setSpotRef("section-4", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Pacific Spirit Regional Park</strong>{" "}
              <Marker
                coordY={49.25647}
                coordX={-123.21885}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located on the University of British Columbia’s peninsula, this vast park offers second-growth rainforest habitat. You’ll find woodpeckers, Pacific Wrens, and warblers in the cedar-hemlock woods. Owls like the Barred Owl are known to nest here. The Foreshore Trail near Burrard Inlet may yield shorebirds and waterfowl. Stick to well-marked trails, as the park is large and easy to get lost in.
            </p>

            {/* SECTION: Iona Beach Regional Park */}
            <h2 id="section-5">Iona Beach Regional Park</h2>
            <p ref={(el) => setSpotRef("section-5", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Iona Beach Regional Park</strong>{" "}
              <Marker
                coordY={49.21700}
                coordX={-123.19800}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located in Richmond, near Vancouver International Airport, Iona Beach features tidal mudflats, a sandy jetty, and sewage treatment ponds that attract a remarkable variety of shorebirds and waterfowl. Over 280 bird species have been recorded here. During migrations, look for Western Sandpipers, Dunlin, and more. Dress warmly and be prepared for wind along the jetty.
            </p>

            {/* SECTION: Reifel Migratory Bird Sanctuary */}
            <h2 id="section-6">George C. Reifel Migratory Bird Sanctuary</h2>
            <p ref={(el) => setSpotRef("section-6", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>George C. Reifel Migratory Bird Sanctuary</strong>{" "}
              <Marker
                coordY={49.09750}
                coordX={-123.16611}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A short drive from Vancouver in Delta, this 850-acre protected wetland is a prime spot to view massive flocks of Snow Geese in winter, Sandhill Cranes, and various ducks. Featuring trails, bird blinds, and an observation tower, Reifel requires an admission fee and online reservation during peak times. No pets are allowed.
            </p>

            {/* SECTION: Maplewood Flats */}
            <h2 id="section-7">Maplewood Flats Conservation Area</h2>
            <p ref={(el) => setSpotRef("section-7", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Maplewood Flats Conservation Area</strong>{" "}
              <Marker
                coordY={49.30611}
                coordX={-123.00500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – On the North Shore of Burrard Inlet, Maplewood Flats is managed by the Wild Bird Trust of BC. Its saltwater marsh, mudflats, and second-growth forest host waterfowl, shorebirds, and songbirds. Trail boardwalks lead to platforms overlooking the inlet, ideal for spotting ducks and wintering trumpeter swans. No dogs and no bikes are allowed; donations are welcome.
            </p>

            {/* SECTION: Burnaby Lake Regional Park */}
            <h2 id="section-8">Burnaby Lake Regional Park</h2>
            <p ref={(el) => setSpotRef("section-8", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Burnaby Lake Regional Park</strong>{" "}
              <Marker
                coordY={49.24243}
                coordX={-122.94607}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – East of Vancouver, Burnaby Lake offers an 11-km trail around a large freshwater lake. Look for Bald Eagles, Ospreys, and Belted Kingfishers, plus multiple duck species in winter. Piper Spit features a boardwalk and viewing platform. The park is open dawn to dusk, with multiple parking lots and SkyTrain access nearby.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Bird-watching in Vancouver is generally open to all, but it’s important to follow certain guidelines to protect both wildlife and habitats:
            </p>
            <ul>
              <li>Avoid feeding wildlife, which can lead to health issues for the birds and dependence on humans.</li>
              <li>Keep a respectful distance: use binoculars or zoom lenses rather than approaching nesting sites.</li>
              <li>Leash pets or leave them at home. Many sanctuaries do not allow pets, and dogs can scare birds off.</li>
              <li>Stay on designated trails to prevent habitat destruction or disturbing ground-nesting birds.</li>
              <li>Follow signage and park rules regarding hours, closed areas, or seasonal restrictions.</li>
              <li>Remember that disturbing or harming birds, their nests, or eggs is illegal under provincial and federal law.</li>
            </ul>

            {/* SECTION: Bird-Watching Tips */}
            <h2 id="tips">Bird-Watching Tips</h2>
            <p>
              Make the most of your birding trips with these recommendations:
            </p>
            <ul>
              <li><strong>Go early or late:</strong> Birds are more active during dawn and dusk.</li>
              <li><strong>Use resources:</strong> Apps like eBird or check local bird-alert forums for recent sightings.</li>
              <li><strong>Bring proper gear:</strong> Binoculars, comfortable footwear, layered clothing for changing weather, and a field guide or ID app.</li>
              <li><strong>Be patient and quiet:</strong> Birds often appear if you stand still and minimize noise.</li>
              <li><strong>Learn calls and songs:</strong> Identifying birds by sound often helps locate them in dense foliage.</li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need a permit or special pass to bird-watch in Vancouver?</h3>
              <p>
                Generally no. Most parks (e.g., Stanley Park, Queen Elizabeth Park) are free. Some places like the Reifel Sanctuary do charge a small admission fee and require reservations during peak times.
              </p>

              <h3>2. What is the best time of year to go bird-watching here?</h3>
              <p>
                Any time! Spring brings migratory songbirds; fall offers another wave of migration; winter hosts large flocks of waterfowl and eagles; and summer highlights nesting birds with their young.
              </p>

              <h3>3. Can I bring my dog along?</h3>
              <p>
                It’s generally not recommended. Dogs must be on leash in most parks and are banned entirely in some sanctuaries like Reifel and Maplewood Flats. Dogs can disturb or scare birds away.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s diverse habitats, from urban parks to coastal wetlands, make it an ideal bird-watching destination year-round. By following local regulations and best practices, you can enjoy watching these incredible creatures while minimizing any impact on their environment. Now that you know where to go and what to expect, grab your binoculars and explore the city’s rich avian life.
            </p>
            <p><em>Disclaimer: Always check official park resources for up-to-date regulations, weather conditions, and any seasonal closures before heading out.</em></p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}